import React from 'react';
import PropTypes from 'prop-types';
import {SectionTitle, InfoItem} from '@/components/molecules';
import {DataTable, Icon} from '@/components/atoms';
import {COLOR_CODE} from '@/utils/constants/colors';
import {
  REWARD_TYPES,
  REWARD_TYPES_DISPLAY
} from '@/utils/constants/rewardsConstants';
import {DigitalCodesCard} from '@/containers/Rewards/RewardSetup/ui-components';
import {renderPreviewContent} from '@/utils/rewardsDisplay';
import {useAppSelector} from '@/store';
import {
  SECTION_TITLES,
  FIELD_LABELS,
  DEFAULT_VALUES,
  INFO_MESSAGES,
  DATA_TABLE_PROPS,
  TABLE_COLUMNS,
  CSS_CLASSES
} from '../common/ReviewConstants';
import './setupsection.scss';

// Cell renderer components defined outside to avoid recreation on each render
const ProductIdCell = ({ctx}) => {
  const id = ctx?.row?.original?.productId;
  // TODO: New flow in progress - disabling clickable link until complete
  // return (
  //   <Link
  //     href={`${URL_PATTERNS.PRODUCT_DETAIL}/${id}`}
  //     target={LINK_TARGETS.SELF}
  //     style={{ color: COLOR_CODE._3387DF }}
  //   >
  //     {id}
  //   </Link>
  // )
  return <span>{id}</span>;
};

ProductIdCell.propTypes = {
  ctx: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.shape({
        productId: PropTypes.string
      })
    })
  }).isRequired
};

const ProductDetailsCell = ({ctx}) => {
  const details = ctx.row.original.details;
  if (!details) return <span>{DEFAULT_VALUES.FALLBACK}</span>;

  return (
    <div className='flex flex-col gap-1 text-sm'>
      <div>Points: {details.points || DEFAULT_VALUES.FALLBACK}</div>
      <div>
        Quota:{' '}
        {details.quota?.unlimited
          ? DEFAULT_VALUES.UNLIMITED
          : details.quota?.value || DEFAULT_VALUES.FALLBACK}
      </div>
      <div>Inventory: {details.inventory || DEFAULT_VALUES.FALLBACK}</div>
    </div>
  );
};

ProductDetailsCell.propTypes = {
  ctx: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.shape({
        details: PropTypes.shape({
          points: PropTypes.number,
          quota: PropTypes.shape({
            unlimited: PropTypes.bool,
            value: PropTypes.number
          }),
          inventory: PropTypes.number
        })
      })
    })
  }).isRequired
};

const ProductPriorityCell = ({ctx}) => <span>{ctx.row.original.priority}</span>;

ProductPriorityCell.propTypes = {
  ctx: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.shape({
        priority: PropTypes.number
      })
    })
  }).isRequired
};

// Columns definition moved outside component to avoid recreation on each render
const getTableColumns = () => [
  {
    ...TABLE_COLUMNS.PRODUCTS.PRODUCT_ID,
    cellData: (ctx) => <ProductIdCell ctx={ctx} />
  },
  TABLE_COLUMNS.PRODUCTS.PRODUCT_NAME,
  {
    ...TABLE_COLUMNS.PRODUCTS.DETAILS,
    cellData: (ctx) => <ProductDetailsCell ctx={ctx} />
  },
  {
    ...TABLE_COLUMNS.PRODUCTS.PRIORITY,
    cellData: (ctx) => <ProductPriorityCell ctx={ctx} />
  }
];

const SetupSection = ({formData}) => {
  const countryCode = useAppSelector((state) => state.countryCode)?.countryCode;

  const getPreviewText = () => {
    const previewText = renderPreviewContent({formData, countryCode});
    return previewText || DEFAULT_VALUES.FALLBACK;
  };

  const columns = getTableColumns();

  return (
    <div>
      <div className='overview reward'>
        <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
          <SectionTitle
            id='reward_conditions'
            label={SECTION_TITLES.SETUP.REWARD_CONDITIONS}
          />
        </div>
        <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
          <InfoItem
            label={FIELD_LABELS.SETUP.REWARD_TYPE}
            value={
              REWARD_TYPES_DISPLAY[formData?.rewardType] ||
              DEFAULT_VALUES.FALLBACK
            }
          />
          {/* VISA_CARD-specific field */}
          {formData?.rewardType === REWARD_TYPES.VISA_CARD && (
            <InfoItem
              label={FIELD_LABELS.SETUP.CARD_TYPE}
              value={formData?.cardType || DEFAULT_VALUES.FALLBACK}
            />
          )}
          <InfoItem
            label={
              formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS
                ? FIELD_LABELS.SETUP.REWARD_NAME
                : FIELD_LABELS.SETUP.NAME
            }
            value={formData?.name || DEFAULT_VALUES.FALLBACK}
          />
          <InfoItem
            label={FIELD_LABELS.SETUP.REWARD_ID}
            value={formData?.rewardId || DEFAULT_VALUES.FALLBACK}
          />
          <InfoItem
            label={FIELD_LABELS.SETUP.REWARD}
            value={getPreviewText()}
          />
          {/* Loyalty-specific fields */}
          {formData?.rewardType === REWARD_TYPES.LOYALTY && (
            <>
              <InfoItem
                label={FIELD_LABELS.SETUP.LOYALTY_URL}
                value={formData?.loyaltyUrl || DEFAULT_VALUES.FALLBACK}
              />
              <InfoItem
                label={FIELD_LABELS.SETUP.COUPON_BASED_LOYALTY_REWARD}
                value={
                  formData?.couponBasedLoyaltyReward
                    ? DEFAULT_VALUES.YES
                    : DEFAULT_VALUES.NO
                }
              />
            </>
          )}
        </div>

        {formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS &&
          formData?.fetchedProducts?.length > 0 && (
            <>
              <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
                <SectionTitle
                  id='products'
                  label={SECTION_TITLES.SETUP.PRODUCTS}
                />
              </div>
              <div
                className='reward-products'
                data-testid='products-scrollable-container'
                style={{
                  maxHeight: '600px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  border: '1px solid #374151',
                  backgroundColor: '#1f2937'
                }}>
                <DataTable
                  columns={columns}
                  data={formData?.fetchedProducts || []}
                  {...DATA_TABLE_PROPS.DEFAULT}
                />
              </div>
            </>
          )}

        {/* Digital Codes Card - Show for Digital, Digital Pre-order, and Loyalty rewards with couponBasedLoyaltyReward = true */}
        {(formData?.rewardType === REWARD_TYPES.DIGITAL ||
          formData?.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER ||
          (formData?.rewardType === REWARD_TYPES.LOYALTY &&
            formData?.couponBasedLoyaltyReward)) && (
          <>
            <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
              <SectionTitle
                id='digital_codes'
                label={SECTION_TITLES.SETUP.DIGITAL_CODES}
              />
            </div>
            <DigitalCodesCard inReview={true} />
          </>
        )}

        <div className={CSS_CLASSES.COMMON.SECTION_BORDER}>
          <SectionTitle id='segments' label={SECTION_TITLES.SETUP.SEGMENTS} />
        </div>
        <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
          <InfoItem
            label={FIELD_LABELS.SETUP.ALL_CUSTOMERS}
            value={
              formData?.allCustomers ? DEFAULT_VALUES.YES : DEFAULT_VALUES.NO
            }
          />
        </div>
        {!formData?.allCustomers && (
          <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
            {formData?.selectedSegments?.length > 0 ? (
              formData.selectedSegments.map((segment, index) => (
                <InfoItem
                  key={`segment-${segment?.join('-') || index}`}
                  label={`Group ${index + 1}`}
                  value={segment?.join(', ') || DEFAULT_VALUES.FALLBACK}
                />
              ))
            ) : (
              <InfoItem
                label={FIELD_LABELS.SETUP.GROUPS}
                value={DEFAULT_VALUES.NO_SEGMENTS}
              />
            )}
          </div>
        )}
        <div className={CSS_CLASSES.COMMON.GRID_3_COLS}>
          <InfoItem
            label={FIELD_LABELS.SETUP.EXCLUDING}
            value={formData?.excludedSegments?.join(', ') || DEFAULT_VALUES.NO}
          />
        </div>
        <div className='flex pb-6 pl-4' style={{color: COLOR_CODE._D0D0D0}}>
          <Icon icon='info' size='small' />
          <span className='ml-2'>{INFO_MESSAGES.SEGMENTS_WARNING}</span>
        </div>
      </div>
    </div>
  );
};

SetupSection.propTypes = {
  formData: PropTypes.shape({
    rewardType: PropTypes.string,
    cardType: PropTypes.string,
    name: PropTypes.string,
    rewardId: PropTypes.string,
    loyaltyUrl: PropTypes.string,
    couponBasedLoyaltyReward: PropTypes.bool,
    allCustomers: PropTypes.bool,
    selectedSegments: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
    excludedSegments: PropTypes.arrayOf(PropTypes.string),
    fetchedProducts: PropTypes.arrayOf(
      PropTypes.shape({
        productId: PropTypes.string,
        productName: PropTypes.string,
        details: PropTypes.shape({
          points: PropTypes.number,
          quota: PropTypes.shape({
            unlimited: PropTypes.bool,
            value: PropTypes.number
          }),
          inventory: PropTypes.number
        }),
        priority: PropTypes.number
      })
    ),
    display: PropTypes.shape({
      productDisplay: PropTypes.object
    })
  }).isRequired
};

export default SetupSection;
